import { request } from '@koi-design/vix-tools';
import { message } from '@koi-design/uxd-ui';
import store from '@/store';

// The voice task center lives on the telephony gateway, which is a separate
// service with its own response envelope: `{ code: 0, message, data }` instead
// of the platform `000000` code, and it expects the tenant in `X-Homeland-ID`.
// `catchAll` keeps the raw response (also for 4xx) so the gateway business code
// stays readable - some flows branch on it, e.g. 40918 asks the user to confirm
// a number-pool deletion.
const telephonyUrl = path => `/api/v1/telephony${path}`;

const homelandHeader = () => ({
  'X-Homeland-ID': store.getters?.homelandInfo?.homeland || ''
});

const telephonyRequest = async config => {
  let response;
  try {
    response = await request({
      ...config,
      catchAll: true,
      // The gateway answers 4xx with a JSON body carrying code and message.
      validateStatus: () => true,
      headers: { ...homelandHeader(), ...config.headers }
    });
  } catch (error) {
    throw new Error(typeof error === 'string' ? error : error?.message);
  }
  const payload = response?.data || {};
  if (payload.code === 0) {
    return { data: payload.data, responseMessage: payload.message };
  }
  const error = new Error(payload.message || `HTTP ${response?.status}`);
  error.code = payload.code;
  throw error;
};

// Read helper: pages only need the unwrapped payload.
const telephonyGet = async config => (await telephonyRequest(config)).data;

// The contract declares every list below as an array. Normalizing once at this
// boundary keeps a mis-shaped payload from reaching a table or a render path,
// where a missing `.find`/`.map` would break the page. The warning keeps a real
// contract mismatch visible instead of silently rendering an empty list.
const asArray = value => {
  if (Array.isArray(value)) return value;
  if (value != null) {
    console.warn('[voiceTaskCenter] expected an array payload', value);
  }
  return [];
};

const asPaginatedList = data => ({
  ...data,
  items: asArray(data?.items)
});

const withPoolNumbers = pool => ({ ...pool, numbers: asArray(pool?.numbers) });

/**
 * Mutation helper mirroring `packApi` feedback for the telephony envelope. Both
 * the success and the failure text come from the gateway `message`, never from
 * copy invented here. `silentCodes` suppresses the toast when the caller owns
 * the interaction (40918 opens a confirmation dialog first).
 */
const telephonyPack = async (config, silentCodes = []) => {
  try {
    const { data, responseMessage } = await telephonyRequest(config);
    message.success(responseMessage);
    return data;
  } catch (error) {
    if (!silentCodes.includes(error.code)) {
      message.error(error.message);
    }
    throw error;
  }
};

// F01 - candidate numbers of the platform pool.
export const getCandidateNumbersApi = async data =>
  asArray(
    await telephonyGet({
      url: telephonyUrl('/numbers/candidates'),
      method: 'get',
      data
    })
  );

// F02 - apply (assign) a candidate number to the current tenant.
export const applyNumberApi = data =>
  telephonyPack(
    { url: telephonyUrl('/numbers/apply'), method: 'post', data },
    [40901]
  );

// F03 - tenant numbers with pagination.
export const getNumberListApi = async data =>
  asPaginatedList(
    await telephonyGet({ url: telephonyUrl('/numbers'), method: 'get', data })
  );

// F04 - enable / disable a tenant number.
export const updateNumberStatusApi = (id, data) =>
  telephonyPack({ url: telephonyUrl(`/numbers/${id}/status`), method: 'put', data });

// F05 - release a number back to the platform pool.
export const releaseNumberApi = id =>
  telephonyPack({ url: telephonyUrl(`/numbers/${id}/release`), method: 'post' });

// The card summary needs the tenant-wide counts, which the list API only gives
// per status filter; page_size 1 keeps it a cheap read.
export const getNumberSummaryApi = async () => {
  const [enabled, disabled] = await Promise.all([
    getNumberListApi({ status: 1, page: 1, page_size: 1 }),
    getNumberListApi({ status: 2, page: 1, page_size: 1 })
  ]);
  return {
    enabled: enabled?.total || 0,
    disabled: disabled?.total || 0
  };
};

// The pool member picker needs every tenant number; the list API caps page_size
// at 100, so keep reading pages until the reported total is collected.
export const getAllNumbersApi = async () => {
  const pageSize = 100;
  const first = await getNumberListApi({ page: 1, page_size: pageSize });
  const items = [...(first?.items || [])];
  const total = first?.total || 0;
  for (let page = 2; items.length < total; page += 1) {
    const next = await getNumberListApi({ page, page_size: pageSize });
    items.push(...(next?.items || []));
  }
  return items;
};

// F06 - number pool list; each row is a PoolDetail carrying its members.
export const getPoolListApi = async data => {
  const result = await telephonyGet({
    url: telephonyUrl('/pools'),
    method: 'get',
    data
  });
  return { ...result, items: asArray(result?.items).map(withPoolNumbers) };
};

// F07 - create a number pool.
export const createPoolApi = data =>
  telephonyPack({ url: telephonyUrl('/pools'), method: 'post', data });

// F08 - number pool detail, includes `numbers` and `tasks_count`.
export const getPoolDetailApi = async id => {
  const data = await telephonyGet({ url: telephonyUrl(`/pools/${id}`), method: 'get' });
  return { ...data, numbers: asArray(data?.numbers) };
};

// F09 - replace a number pool (members are a full replacement).
export const updatePoolApi = (id, data) =>
  telephonyPack({ url: telephonyUrl(`/pools/${id}`), method: 'put', data });

// F10 - delete a number pool. The gateway answers 40918 while the pool is still
// referenced by tasks; the caller re-sends with `confirm = true` once the user
// accepts the impact.
export const deletePoolApi = (id, confirm = false) =>
  telephonyPack(
    {
      url: telephonyUrl(`/pools/${id}`),
      method: 'delete',
      data: confirm ? { confirm: true } : {}
    },
    [40918]
  );

// F11 - outbound task list.
export const getTaskListApi = async data =>
  asPaginatedList(
    await telephonyGet({ url: telephonyUrl('/tasks'), method: 'get', data })
  );

// F12 - create an outbound task.
export const createTaskApi = data =>
  telephonyPack({ url: telephonyUrl('/tasks'), method: 'post', data });

// F13 - outbound task detail.
export const getTaskDetailApi = taskId =>
  telephonyGet({ url: telephonyUrl(`/tasks/${taskId}`), method: 'get' });

// F14 - update an outbound task (full form payload).
export const updateTaskApi = (taskId, data) =>
  telephonyPack({ url: telephonyUrl(`/tasks/${taskId}`), method: 'put', data });

// F15 - pause / resume an outbound task.
export const updateTaskStatusApi = (taskId, data) =>
  telephonyPack({ url: telephonyUrl(`/tasks/${taskId}/status`), method: 'put', data });

// F16 - delete an outbound task (paused tasks only).
export const deleteTaskApi = taskId =>
  telephonyPack({ url: telephonyUrl(`/tasks/${taskId}`), method: 'delete' });

// F17 - rotate the task API key; the previous key stops working immediately.
export const resetTaskKeyApi = taskId =>
  telephonyPack({ url: telephonyUrl(`/tasks/${taskId}/reset_key`), method: 'post' });

// F18 - process variables of the selected agent, two groups.
export const getAgentVariablesApi = async robotId => {
  const data = await telephonyGet({
    url: telephonyUrl(`/agents/${robotId}/variables`),
    method: 'get'
  });
  return {
    ...data,
    agent_variables: asArray(data?.agent_variables),
    flow_variables: asArray(data?.flow_variables)
  };
};

// Q1 - calls of a task.
export const getTaskCallListApi = async (taskId, data) =>
  asPaginatedList(
    await telephonyGet({
      url: telephonyUrl(`/tasks/${taskId}/calls`),
      method: 'get',
      data
    })
  );

// Q2 - single call detail with recording, transcript and runtime variables.
export const getCallDetailApi = (taskId, callId) =>
  telephonyGet({
    url: telephonyUrl(`/tasks/${taskId}/calls/${callId}`),
    method: 'get'
  });

// The voice task center has its own white list on the telephony gateway.
export const getVoiceTaskCenterWhitelistApi = () =>
  telephonyGet({ url: telephonyUrl('/auth_white_list'), method: 'get' });

// The gateway does not return the push address, the caller splices it from the
// public base url (same-origin reverse proxy per the integration contract).
export const getOutcallWebhookUrl = taskId =>
  `${window.location.origin}/webhook/outcall/${taskId}`;
